import { useEffect, useState } from 'react'

interface MSWState {
  isActive: boolean
  isDisabled: boolean
  scenario: string | null
  worker: typeof import('@/mocks/browser').worker | null
}

const MSW_DISABLED_STORAGE_KEY = 'skoghub:msw:disabled'
const MSW_LAST_SCENARIO_STORAGE_KEY = 'skoghub:msw:lastScenario'

const getScenarioFromURL = (): string | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('scenario')
}

const readIsDisabledFromStorage = (): boolean => {
  if (!import.meta.env.DEV) {
    return false
  }
  if (typeof window === 'undefined') {
    return false
  }

  return window.localStorage.getItem(MSW_DISABLED_STORAGE_KEY) === 'true'
}

const writeIsDisabledToStorage = (value: boolean) => {
  if (!import.meta.env.DEV) {
    return
  }
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(MSW_DISABLED_STORAGE_KEY, value ? 'true' : 'false')
}

const readLastScenarioFromStorage = (): string | null => {
  if (!import.meta.env.DEV) {
    return null
  }
  if (typeof window === 'undefined') {
    return null
  }

  const value = window.localStorage.getItem(MSW_LAST_SCENARIO_STORAGE_KEY)
  return value && value.trim().length > 0 ? value : null
}

const writeLastScenarioToStorage = (scenario: string) => {
  if (!import.meta.env.DEV) {
    return
  }
  if (typeof window === 'undefined') {
    return
  }
  window.localStorage.setItem(MSW_LAST_SCENARIO_STORAGE_KEY, scenario)
}

const hasMockServiceWorkerRegistration = async (): Promise<boolean> => {
  if (!import.meta.env.DEV) {
    return false
  }
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return false
  }

  const registrations = await navigator.serviceWorker.getRegistrations()
  return registrations.some((reg) => {
    const scriptUrl = reg.active?.scriptURL ?? reg.installing?.scriptURL ?? reg.waiting?.scriptURL
    return typeof scriptUrl === 'string' && scriptUrl.includes('mockServiceWorker.js')
  })
}

export const useMSW = () => {
  const [mswState, setMswState] = useState<MSWState>({
    isActive: false,
    isDisabled: false,
    scenario: null,
    worker: null,
  })

  useEffect(() => {
    const scenario = getScenarioFromURL()
    const isDisabled = readIsDisabledFromStorage()
    const lastScenario = readLastScenarioFromStorage()

    setMswState((prev) => {
      return {
        ...prev,
        scenario: scenario ?? lastScenario,
        isDisabled,
      }
    })

    if (import.meta.env.DEV) {
      void (async () => {
        try {
          const isRegistered = await hasMockServiceWorkerRegistration()
          if (!isRegistered) {
            return
          }

          setMswState((prev) => ({
            ...prev,
            isActive: true,
            scenario: prev.scenario ?? lastScenario,
          }))
        } catch (error) {
          console.warn('[MSW] Failed to detect service worker registration:', error)
        }
      })()
    }

    if (scenario && import.meta.env.DEV) {
      void (async () => {
        try {
          // Scenario URL should always activate MSW (DEV-only)
          writeIsDisabledToStorage(false)
          writeLastScenarioToStorage(scenario)

          const mocksModule = await import('@/mocks/browser')
          const mswWorker = mocksModule.worker

          await mswWorker.start({
            onUnhandledRequest: 'bypass',
            serviceWorker: {
              url: '/mockServiceWorker.js',
            },
          })

          setMswState({
            isActive: true,
            isDisabled: false,
            scenario,
            worker: mswWorker,
          })

          console.log('[MSW] ✅ Mock Service Worker started')
          console.log(`[MSW] 📋 Active scenario: ${scenario}`)
        } catch (error) {
          console.warn('[MSW] Failed to start mock service worker:', error)
        }
      })()
    }

    return () => {
      // Cleanup if component unmounts
    }
  }, [])

  const stop = async () => {
    try {
      mswState.worker?.stop()
      writeIsDisabledToStorage(true)

      setMswState({
        isActive: false,
        isDisabled: true,
        scenario: mswState.scenario,
        worker: null,
      })

      // Also unregister the service worker
      if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations()
        await Promise.all(
          registrations.map(async (reg) => {
            const scriptUrl =
              reg.active?.scriptURL ?? reg.installing?.scriptURL ?? reg.waiting?.scriptURL
            if (typeof scriptUrl === 'string' && scriptUrl.includes('mockServiceWorker.js')) {
              await reg.unregister()
            }
          }),
        )
      }

      // Remove scenario query parameter from URL before reloading
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href)
        url.searchParams.delete('scenario')
        window.history.replaceState({}, '', url.toString())
      }

      console.log('[MSW] ✅ Mock Service Worker stopped and unregistered')
      window.location.reload()
    } catch (error) {
      console.error('[MSW] Failed to stop service worker:', error)
    }
  }

  return {
    isActive: mswState.isActive,
    isDisabled: mswState.isDisabled,
    scenario: mswState.scenario,
    stop,
  }
}
